import { forwardRef, type CSSProperties, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/** Text input with the canonical 3px accent focus ring. */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn("input", className)} {...props} />;
  },
);

/** Inline-editable value used inside review field cells (borderless variant). */
export const EditValue = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(
  function EditValue({ className, mono, ...props }, ref) {
    return (
      <input ref={ref} className={cn("editval", mono && "editval--mono", className)} {...props} />
    );
  },
);

/**
 * Toggle — boolean switch. `md` for settings (accent on), `mini` for inline
 * overlays (pass `onColor` to set the on-state colour per context, e.g. bad).
 */
export function Toggle({
  checked,
  onChange,
  label,
  size = "md",
  onColor,
  className,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  size?: "md" | "mini";
  onColor?: string;
  className?: string;
  disabled?: boolean;
}) {
  const base = size === "mini" ? "mini-tog" : "tog";
  const style = onColor ? ({ "--mt-on": onColor } as CSSProperties) : undefined;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cn(base, checked && `${base}--on`, className)}
      style={style}
      onClick={() => onChange(!checked)}
    />
  );
}
